import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import type { Product } from '@/mocks/products';
import type { UserRole } from '@/hooks/useAuth';

interface DbPrice {
  id: string;
  price_vip: number;
  price_partner: number;
  box_price_partner: number;
  box_quantity: number;
}

/**
 * DB 가격으로 mock 상품 가격을 오버라이드
 * - price_partner: 파트너 단가 (어드민이 설정한 실제 파트너 판매가)
 * - box_price_partner: 파트너 박스가
 * - price_vip: 원가 (내부 원가, 파트너에게 노출 안 함)
 * unitPrice에는 반드시 price_partner를 사용해야 파트너가 올바른 가격을 봄
 */
export function mergeDbPrices(products: Product[], dbPrices: DbPrice[], _role: UserRole | null): Product[] {
  const priceMap = new Map(dbPrices.map(p => [p.id, p]));
  return products.map(product => {
    const db = priceMap.get(product.id);
    if (!db) return product;
    return {
      ...product,
      // ✅ 파트너 단가: price_partner 우선, 없으면 mock 유지
      unitPrice: db.price_partner > 0 ? Number(db.price_partner) : product.unitPrice,
      // ✅ 파트너 박스가: box_price_partner 우선, 없으면 mock 유지
      boxPrice: db.box_price_partner > 0 ? Number(db.box_price_partner) : product.boxPrice,
      // ✅ 박스 수량: DB 값 우선
      boxQuantity: db.box_quantity > 0 ? db.box_quantity : product.boxQuantity,
    };
  });
}

// 여러 상품의 DB 가격을 한번에 가져오는 훅
export function useDbPrices(productIds: string[]) {
  const [dbPrices, setDbPrices] = useState<DbPrice[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (productIds.length === 0) {
      setLoading(false);
      return;
    }
    const fetchPrices = async () => {
      const { data } = await supabase
        .from('products')
        .select('id, price_vip, price_partner, box_price_partner, box_quantity')
        .in('id', productIds);
      if (data) setDbPrices(data);
      setLoading(false);
    };
    fetchPrices();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productIds.join(',')]);

  return { dbPrices, loading };
}

/**
 * 역할에 따른 표시 가격 계산
 * mergeDbPrices 이후 product.unitPrice/boxPrice는 이미 파트너가로 설정되어 있으므로
 * 추가 마진 계산 없이 그대로 반환
 */
export function getDisplayPrice(product: Product, _role: UserRole | null): { unitPrice: number; boxPrice: number } {
  return {
    unitPrice: product.unitPrice,
    boxPrice: product.boxPrice,
  };
}
